/**
 * GapDuration MDXEditor plugin — a section's time allocation, right-aligned inside its heading.
 *
 * MARKDOWN SYNTAX (textDirective for inline use):
 *   ## Welcome :GapDuration{value="5"}
 *
 * Renders as "5 mins" at the heading's right edge; while editing, clicking it opens the duration picker, which can also remove it.
 */

import { DirectiveDescriptor, DirectiveEditorProps, useMdastNodeUpdater, useLexicalNodeRemove, useCellValue, readOnly$ } from '@mdxeditor/editor'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { themeConfig } from '@gap/shared/theme'
import useStore from 'reusable/mobx-store-utils'
import DurationSubmenuPopover from '../../popover/DurationSubmenuPopover'
import GapDurationStore from './GapDurationStore'

const DIRECTIVE_NAME = 'GapDuration'

const labelSx = {
   position: 'absolute',
   right: 0,
   top: '0.75em',
   whiteSpace: 'nowrap',
   color: themeConfig.primary['500'],
   fontWeight: 400,
   fontSize: '0.875rem',
} as const

const GapDurationComponent = observer(function GapDurationComponent({ mdastNode }: DirectiveEditorProps) {
   const isReadOnly = useCellValue(readOnly$)
   const updateMdastNode = useMdastNodeUpdater()
   const removeNode = useLexicalNodeRemove()
   const chipRef = useRef<HTMLSpanElement>(null)

   const gapDurationStore = useStore(sp => new GapDurationStore(sp), {
      updateMdastNode,
      removeNode,
      attributes: mdastNode.attributes as Record<string, string> | undefined,
      chipRef,
   })

   // Pads the heading so its text cannot run under the label.
   useEffect(() => {
      const heading = chipRef.current?.closest('h1, h2, h3, h4, h5, h6') as HTMLElement | null
      if (heading) heading.style.paddingRight = '3em'
      return () => {
         if (heading) heading.style.paddingRight = ''
      }
   }, [])

   if (!gapDurationStore.label) return null

   if (isReadOnly) {
      return <Box component="span" ref={chipRef} contentEditable={false} sx={labelSx}>{gapDurationStore.label}</Box>
   }

   return (
      <>
         <Tooltip title="Change duration">
            <Box
               component="span"
               ref={chipRef}
               contentEditable={false}
               onMouseDown={gapDurationStore.handleChipMouseDown}
               sx={{ ...labelSx, cursor: 'pointer', '&:hover': { color: themeConfig.primary['800'] } }}
            >
               {gapDurationStore.label}
            </Box>
         </Tooltip>
         {gapDurationStore.popover && (
            <DurationSubmenuPopover
               anchor={gapDurationStore.popover}
               onSelect={gapDurationStore.handlePicked}
               onRemove={gapDurationStore.handleRemove}
               onClose={gapDurationStore.handleClose}
            />
         )}
      </>
   )
})

export const GapDurationDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'textDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: ['value'],
   hasChildren: false,
   Editor: GapDurationComponent,
}
